import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getConfig } from '@klpkonsern/appconfig';
import { Toast } from '@klpkonsern/ui';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block } from '@klp/design';
import { Layout } from '@klp/layout';

import { useCurrentUser } from '../auth';
import { Breadcrumbs } from '../packages/breadcrumbs';
import { getSoLinkWithClientId } from '../packages/common';
import { AppDefinition } from '../packages/core/app/classes/AppDefinition';
import { ErrorHandler } from '../packages/core/app/views/ErrorHandler';
import { useKlpStyles } from '../packages/design/Theme/useKlpStyles';
import { Header } from '../packages/layout/Header';
import { Sidebar } from '../packages/layout/Sidebar';
import { generatePath } from '../packages/routing';
import { updateUser } from '../packages/user/intercom';
import { useGetUserQuery } from '../packages/user/rtkApi';
import { AuthorisedRoutes, UnauthorisedRoutes } from '../routing';

import '@klpkonsern/ui/dist/style.css';

import {
    menuItems,
    secondaryMenuItems,
    sustainabilityMenuItems,
} from './MenuItems';

const styles = () => ({
    mainBlock: {
        minHeight: '100vh',
    },
});
export const MittleieforholdApp: React.FC = () => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const { currentUser } = useCurrentUser();
    const { selectedClient } = useAppParams();

    const { data: user } = useGetUserQuery(undefined, {
        skip: !currentUser,
    });

    // Update Intercom when user data is fetched
    useEffect(() => {
        if (user) {
            updateUser(user);
        }
    }, [user]);

    const sidebarProps = {
        menuItems,
        secondaryMenuItems,
        sustainabilityMenuItems,
    };
    const appSelection = [
        new AppDefinition({
            name: t('app.mittleieforhold'),
            url: window.location.origin,
        }),
        new AppDefinition({
            name: t('app.forvalter'),
            url: getConfig('WEBAPP_APP_FORVALTER_URL', ''),
        }),
        new AppDefinition({
            name: t('app.d365'),
            url: getConfig('WEBAPP_APP_D365_LINK', ''),
        }),
        new AppDefinition({
            name: t('app.eiendomGpt'),
            url: getConfig('WEBAPP_APP_EIENDOM_GPT_URL', ''),
            openInNewTab: true,
        }),
    ];

    if (currentUser?.isAdmin) {
        appSelection.push(
            new AppDefinition({
                name: t('app.openInSuperOffice'),
                url: selectedClient
                    ? getSoLinkWithClientId(selectedClient.soContactID || 0)
                    : getConfig('WEBAPP_APP_SUPEROFFICE_LINK', ''),
                openInNewTab: true,
            }),
        );
        appSelection.push(
            new AppDefinition({
                name: t('app.verified'),
                url: generatePath(
                    getConfig('WEBAPP_APP_VERIFIED_LINK', ''),
                    '#',
                    'login',
                ),
                openInNewTab: true,
            }),
        );
    }

    return (
        <Block className={classes.mainBlock}>
            <Toast />
            <Layout>
                <ErrorHandler>
                    <Header
                        appSelection={appSelection}
                        breadcrumbs={Breadcrumbs}
                        hideClientSelector={false}
                    />
                    <Sidebar {...sidebarProps} />
                    <UnauthorisedRoutes />
                    <AuthorisedRoutes />
                </ErrorHandler>
            </Layout>
        </Block>
    );
};
